import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { useLoginContext } from '../../../../contexts/LoginContext';
import { createUser, userProfile } from '../../util';
import { EmailNotificationUser } from '../../types';
import { useStepperNavigation } from '../../shared/hooks/useStepperNavigation';
import { steps } from '../constants/steps';

export const useNewAccountState = () => {
  const { user, setError, setUser } = useLoginContext();

  // Use the shared stepper navigation hook (dynamic step count from steps array)
  const stepperNavigation = useStepperNavigation({ totalSteps: steps.length });
  const { setActiveStep, activeStep } = stepperNavigation;

  const [saveError, setSaveError] = useState<string | null>(null);
  const [stepValidations, setStepValidations] = useState<
    Record<number, { message: string; hasError: boolean }>
  >({});

  // Get warning message based on current step validation state
  const getWarningMessage = useCallback(() => {
    const currentStepValidation = stepValidations[activeStep];
    return currentStepValidation?.message || '';
  }, [stepValidations, activeStep]);

  // Check if current step has errors
  const hasCurrentStepError = useCallback(() => {
    const currentStepValidation = stepValidations[activeStep];
    return currentStepValidation?.hasError || false;
  }, [stepValidations, activeStep]);

  const warningMessage = getWarningMessage();
  const showWarning = hasCurrentStepError();

  // Function to update validation for a specific step
  const updateStepValidation = useCallback(
    (stepIndex: number, message: string, hasError: boolean) => {
      setStepValidations((prev) => ({
        ...prev,
        [stepIndex]: { message, hasError },
      }));
    },
    []
  );

  // URL param handling for testing
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const step = searchParams.get('step');
    if (step) {
      setActiveStep(parseInt(step, 10));
    } else {
      setActiveStep(0);
    }
  }, [searchParams, setSearchParams, setActiveStep]);

  const handleSave = useCallback(async () => {
    const exception = await createUser(user);
    if (exception === '') {
      window.location.href = '/notification?created=true';
    } else {
      setSaveError(exception);
      setError(true);
    }
  }, [setError, user]);

  const handleReset = useCallback(() => {
    stepperNavigation.resetToFirstStep();
    setUser(cloneDeep(userProfile) as EmailNotificationUser);
    setSaveError('');
  }, [setUser, stepperNavigation]);

  return {
    ...stepperNavigation,
    saveError,
    setSaveError,
    handleSave,
    handleReset,
    user,
    setError,
    setUser,
    warningMessage,
    showWarning,
    updateStepValidation,
  };
};
